import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import DOMPurify from 'dompurify';
import {
  useCreateComment,
  useDeleteAttachment,
  useGetComments,
} from 'modules/account/contact/hooks';
import {
  useGetProfile,
  useGetSubscription,
} from 'modules/account/settings/hooks';
import dayjs from 'dayjs';
import { BsChatSquareDotsFill, BsFillPencilFill } from 'react-icons/bs';
import {
  Accordion,
  AttachmentsTooLargeModal,
  Comment,
  DeleteItemModal,
} from 'ui/components/common';
import { NavAction } from 'ui/components/nav';
import {
  addClassNameToRichText,
  cn,
  convertToMegabytes,
  downloadURI,
  useAdminParams,
  useUploadToUploadCare,
} from 'utils/helpers';
import { capitalize, compact, startCase, uniqBy } from 'lodash';
import { useCompanyNotesContext } from 'modules/account/contact/containers/Companies/Notes/Notes';
import { useLeadNotesContext } from 'modules/account/contact/containers/Leads/Notes/Notes';
import { useDealNotesContext } from 'modules/account/sales/containers/Deals/Notes/Notes';
import { useLocation } from 'react-router-dom';
import useModal from 'ui/partials/Modal/useModal';
import c, { SIZE } from 'utils/constants';
import { COMMENT_TYPE, Comment as IComment } from 'utils/types';
import { SupportedFileInput } from '@uploadcare/upload-client';
import type { FileRejection } from 'react-dropzone';
import {
  AttachedItemsList,
  ViewAllAttachmentsModal,
} from 'modules/account/contact/components';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';
import { CallLogIcon, SMSLogIcon } from 'ui/components/icon';

import CommentAttachmentCounter from './CommentAttachmentCounter';
import { Attachment } from '../../NotesComment/Comment';
import NoteCommentForm from './NoteCommentForm';

const query = {
  'include[0]': 'comments',
  'include[1]': 'attachments',
  'include[2]': 'accountUser',
};

const useNote = () => ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSelectedNoteId: (_id: string | null) => {},
  selectedNoteId: null,
});

interface PROPS {
  data: IComment & { logType: COMMENT_TYPE };
  open: boolean;
  onDelete: () => void;
  onEdit: (id: string, value: IComment, referenceId: string) => void;
}

const NotesEntry: FC<PROPS> = ({ data, open, onDelete, onEdit }) => {
  const { pathname } = useLocation();

  const useNoteId = useMemo(() => {
    if (pathname.includes('leads')) {
      return useLeadNotesContext;
    }

    if (pathname.includes('companies')) {
      return useCompanyNotesContext;
    }

    if (pathname.includes('deals')) {
      return useDealNotesContext;
    }

    return useNote;
  }, [pathname]);

  const { accountUserId } = useAdminParams();
  const {
    state: { isPreview },
  } = useAppWrapperContext();
  const note = useNoteId();
  const [isOpenNote, setIsOpenNote] = useState(false);
  const { data: profile } = useGetProfile();
  const { showModal } = useModal();
  const [showEditButton, setShowEditButton] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [value, setValue] = useState(data?.content);
  const [formAttachments, setFormAttachments] = useState<Attachment[]>([]);
  const [addCommentValue, setAddCommentValue] = useState('');
  const [addCommentAttachments, setAddCommentAttachments] = useState<
    Attachment[]
  >([]);
  const [showAddComment, setShowAddComment] = useState(false);
  const { mutate: deleteAttachment, isLoading: deleteLoading } =
    useDeleteAttachment();
  const {
    getComments,
    query: { data: fetchedComments },
  } = useGetComments();
  const { mutateAsync: uploadFiles, isLoading } = useUploadToUploadCare();
  const { mutate: addComment } = useCreateComment();
  const { data: subscription } = useGetSubscription();

  const { MAX_UPLOAD_SIZE: maxSize } = c;

  const noOfComments = data?.comments?.total ?? 0;
  const noOfAttachments = data?.attachments?.length;
  const isEdited = data.createdAt !== data.updatedAt;

  const totalFormAttachmentSize = formAttachments?.reduce(
    (acc, curr) => acc + curr.size,
    0,
  );
  const totalAddCommentAttachmentSize = addCommentAttachments?.reduce(
    (acc, curr) => acc + curr.size,
    0,
  );
  const fetchedAttachmentsSize =
    data?.attachments?.reduce((acc, curr) => acc + curr.fileSize, 0) ?? 0;
  const totalNoteAttachmentSize =
    totalFormAttachmentSize + fetchedAttachmentsSize;

  const toggleComments = () => setShowComments((prevVal) => !prevVal);
  const toggleAttachments = () => setShowAttachments((prevVal) => !prevVal);
  const toggleCommentForm = () => {
    setEditing(false);
    setShowAddComment((prevVal) => !prevVal);
  };
  const showNoteEditForm = () => {
    setShowAddComment(false);
    setEditing(true);
    note?.setSelectedNoteId?.(data?.id);
  };

  const isNoteOwner = useMemo(() => {
    if (isPreview) return data?.actor?.actorId === profile?._id;

    return (
      profile?._id === data?.userId?._id || accountUserId === data?.userId?._id
    );
  }, [accountUserId, data, profile, isPreview]);

  useEffect(() => {
    if (showComments && data.id) {
      getComments(data.id, query);
    }
  }, [data.id, getComments, showComments]);

  useEffect(() => {
    note?.setSelectedNoteId?.(showAddComment ? `NEW-COMMENT${data?.id}` : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddComment, data]);

  useEffect(() => {
    setShowComments(isOpenNote);
    setShowAttachments(isOpenNote);

    if (!isOpenNote) {
      setShowAddComment(false);
    }
  }, [isOpenNote]);

  const handleSaveComment = async () => {
    const uploadedAttachments =
      addCommentAttachments.length &&
      (await uploadFiles(
        addCommentAttachments as unknown as SupportedFileInput[],
        {
          onSuccess: () => setAddCommentAttachments([]),
        },
      ));

    addComment({
      content: addCommentValue,
      referenceId: data.referenceId,
      parentId: data.id,
      source: data.source,
      ...(uploadedAttachments && {
        attachments: uploadedAttachments.files.map((file) => ({
          fileUrl: file.cdnUrl,
          fileType: file.mimeType,
          fileName: file.originalFilename,
          fileSize: file.size,
          source: startCase(data.source),
          ...(file.isImage && { fileThumbnail: file.cdnUrl }),
        })),
      }),
    });
    setEditing(addCommentAttachments.length ? !uploadedAttachments : false);
    setAddCommentValue('');
    setShowAddComment(false);
    note?.setSelectedNoteId(null);
  };

  const handleCancelComment = () => {
    setAddCommentValue('');
    setAddCommentAttachments([]);
    setShowAddComment(false);
    setEditing(false);
    note?.setSelectedNoteId(null);
  };

  const handleDeleteAttachment = useCallback(
    (attachmentId: string) => {
      deleteAttachment({
        id: attachmentId,
        noteId: data?._id,
        referenceId: data?.referenceId,
      });
    },
    [data, deleteAttachment],
  );

  const navOptions = compact([
    isNoteOwner && {
      text: 'Edit',
      key: 'edit',
      onClick: showNoteEditForm,
    },
    isNoteOwner && {
      text: 'Delete',
      key: 'delete',
      onClick: onDelete,
    },
  ]);

  const removeAttachmentFromNoteForm = (name: string) => {
    setFormAttachments((prevVal) =>
      prevVal.filter((val) => !(val.name === name)),
    );
  };

  const removeAttachmentFromCommentForm = (name: string) => {
    setAddCommentAttachments((prevVal) =>
      prevVal.filter((val) => !(val.name === name)),
    );
  };

  const showAttachmentDeleteModal = useCallback(
    (attachmentId: string) => {
      showModal({
        title: 'Attachment',
        disableOutsideClick: true,
        content: (
          <DeleteItemModal
            text="attachment"
            onClickDelete={() => handleDeleteAttachment(attachmentId)}
            isLoading={deleteLoading}
            validation
          />
        ),
      });
    },
    [handleDeleteAttachment, showModal, deleteLoading],
  );

  const showAttachmentsTooLargeModal = (fileSize: string) => {
    showModal({
      title: 'Attachments',
      disableOutsideClick: true,
      content: <AttachmentsTooLargeModal fileSize={fileSize} />,
    });
  };

  const handleSave = async () => {
    const uploadedAttachments =
      formAttachments.length &&
      (await uploadFiles(formAttachments as unknown as SupportedFileInput[], {
        onSuccess: () => setAddCommentAttachments([]),
      }));

    await onEdit(
      data.id,
      {
        content: value,
        ...(uploadedAttachments && {
          attachments: uploadedAttachments.files.map((file) => ({
            fileUrl: file.cdnUrl,
            fileType: file.mimeType,
            fileName: file.originalFilename,
            fileSize: file.size,
            source: startCase(data.source),
            ...(file.isImage && { fileThumbnail: file.cdnUrl }),
          })),
        }),
      } as IComment,
      data.referenceId,
    );
    setFormAttachments([]);
    setEditing(false);
    note?.setSelectedNoteId(null);
  };

  const handleCancel = () => {
    setValue(data.content);
    setFormAttachments([]);
    note?.setSelectedNoteId?.(null);
    setEditing(false);
  };

  const onDrop =
    (setAttachments: any) =>
    <T extends File>(_acceptedFiles: T[]) => {
      setAttachments((prevVal: any) =>
        uniqBy([...prevVal, ..._acceptedFiles], 'name'),
      );
      const sizeUploaded = data.attachments?.reduce(
        (acc, curr) => acc + curr.fileSize,
        0,
      );
      const sizeToUpload = [...formAttachments, ..._acceptedFiles].reduce(
        (acc, curr) => acc + curr.size,
        0,
      );
      if ((sizeUploaded || 0) + sizeToUpload > maxSize) {
        showAttachmentsTooLargeModal(
          convertToMegabytes((sizeUploaded || 0) + sizeToUpload),
        );
      }
    };
  const commentsOnDrop = onDrop(setAddCommentAttachments);

  const notesOnDrop = onDrop(setFormAttachments);

  const onDropRejected = (fileRejections: FileRejection[]) => {
    const _totalSize = fileRejections.reduce((acc, { errors, file }) => {
      if (errors[0].code === 'file-too-large') {
        return acc + file.size;
      }
      return acc;
    }, 0);
    if (fileRejections[0].errors[0].code === 'file-too-large') {
      showAttachmentsTooLargeModal(convertToMegabytes(_totalSize));
    }
  };

  const disableAddComment = useMemo(
    () =>
      note?.selectedNoteId && note?.selectedNoteId !== `NEW-COMMENT${data?.id}`,
    [note, data?.id],
  );

  const showAllAttachmentsModal = () => {
    showModal({
      content: (
        <ViewAllAttachmentsModal
          referenceId={data?._id}
          source={'Comment'}
          onPreviewClose={showAllAttachmentsModal}
        />
      ),
      title: 'Attachments',
      size: SIZE.Large,
    });
  };

  const createdBy = useMemo(() => {
    if (!data?.userId && data?.actor) {
      const user = data?.actor?.meta as string;

      if (user) return JSON.parse(user).fullName;

      return null;
    }

    return data?.userId?.fullName;
  }, [data]);

  const logTypeAndDateTime = useMemo(() => {
    if (data.logType === COMMENT_TYPE.GENERAL) return null;
    let icon: any;

    switch (data.logType) {
      case COMMENT_TYPE.CALL: {
        icon = <CallLogIcon className="w-4 h-4 text-primary-base" />;
        break;
      }
      case COMMENT_TYPE.SMS: {
        icon = <SMSLogIcon className="w-4 h-4 text-primary-base" />;
        break;
      }
    }
    return (
      <div className="flex items-center gap-2">
        {icon}

        <span className="text-xs">
          <span className="font-semibold">
            {data.logType === COMMENT_TYPE.CALL
              ? capitalize(data.logType)
              : data.logType}{' '}
            log
          </span>{' '}
          | {dayjs(data.logCreatedAt).format('MMM DD YYYY hh:mm A')}
        </span>
      </div>
    );
  }, [data.logCreatedAt, data.logType]);

  return (
    <Accordion
      onMouseEnter={() => setShowEditButton(true)}
      onMouseLeave={() => setShowEditButton(false)}
      className="text-left py-[18px]"
      label={`Note${createdBy ? ` by ${createdBy}` : ''}`}
      action={
        !subscription?.isExpired && (
          <div
            className={cn(
              'self-start',
              (!isNoteOwner || note?.selectedNoteId) && 'hidden',
            )}
          >
            <NavAction
              className="text-xs"
              label="Actions"
              options={navOptions}
              showArrow
            />
          </div>
        )
      }
      subElement={({ open: isOpen }) => {
        setIsOpenNote(isOpen);

        return (
          <div className="space-y-4 text-left">
            <div className="flex items-center gap-1">
              <p className="block whitespace-pre-line text-xs">
                {dayjs(data?.updatedAt).format('MMM DD YYYY hh:mm A')}
              </p>
              {isEdited && <div className="text-xs">(Updated)</div>}
            </div>
            {logTypeAndDateTime}
            <div className="space-y-[18px]">
              {editing ? (
                <NoteCommentForm
                  value={value}
                  onChange={setValue}
                  name="notes"
                  placeholder="Add Notes"
                  attachments={formAttachments}
                  onRemoveAttachment={removeAttachmentFromNoteForm}
                  disabled={!value || totalNoteAttachmentSize > maxSize}
                  onCancel={handleCancel}
                  onSave={handleSave}
                  onDrop={notesOnDrop}
                  onDropRejected={onDropRejected}
                  isLoading={isLoading}
                />
              ) : (
                <div className="flex items-start gap-x-2">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        addClassNameToRichText('text-sm', data?.content) ?? '',
                      ),
                    }}
                    className={cn(
                      'flex-1',
                      !isOpen && 'line-clamp-2',
                      'break-all',
                    )}
                  />
                  {showEditButton && !subscription?.isExpired ? (
                    <button
                      className={cn(
                        'hover:text-primary',
                        (!isNoteOwner || note?.selectedNoteId) && 'hidden',
                      )}
                      type="button"
                      onClick={showNoteEditForm}
                    >
                      <BsFillPencilFill />
                    </button>
                  ) : (
                    <div
                      className={cn(
                        'h-1 w-[14px]',
                        (!isNoteOwner || note?.selectedNoteId) && 'hidden',
                      )}
                    />
                  )}
                </div>
              )}
              <CommentAttachmentCounter
                attachmentCount={noOfAttachments}
                commentCount={noOfComments}
                toggleAttachments={toggleAttachments}
                toggleComments={toggleComments}
              />
              {showAttachments && data?.attachments && (
                <AttachedItemsList
                  hideNumberOfFilesUploaded
                  attachments={data.attachments}
                  onDownload={downloadURI}
                  showRemove={editing}
                  onRemove={showAttachmentDeleteModal}
                  onClickShowAllAttachments={showAllAttachmentsModal}
                />
              )}
              {(isOpen || showComments) && !subscription?.isExpired && (
                <button
                  onClick={toggleCommentForm}
                  type="button"
                  className={cn(
                    'w-full pt-4 disabled:bg-transparent',
                    'flex hover:text-primary',
                    'text-xs leading-4',
                    'border-t border-t-[#E6F0FF]',
                    'items-center',
                    'font-semibold',
                    disableAddComment &&
                      'cursor-not-allowed text-disabled hover:text-disabled',
                  )}
                  disabled={!!disableAddComment}
                >
                  <BsChatSquareDotsFill
                    className={cn(
                      'mr-[10px] text-primary',
                      disableAddComment && 'text-disabled',
                    )}
                  />
                  <span>ADD COMMENT</span>
                </button>
              )}
              {showAddComment && (
                <NoteCommentForm
                  name="add-comment"
                  placeholder="Add Comment"
                  onChange={setAddCommentValue}
                  attachments={addCommentAttachments}
                  onRemoveAttachment={removeAttachmentFromCommentForm}
                  disabled={
                    !addCommentValue || totalAddCommentAttachmentSize > maxSize
                  }
                  onCancel={handleCancelComment}
                  onSave={handleSaveComment}
                  onDrop={commentsOnDrop}
                  onDropRejected={onDropRejected}
                  isLoading={isLoading}
                />
              )}
            </div>
            {showComments && !!fetchedComments?.length && (
              <div className="flex flex-col gap-4">
                {fetchedComments?.map((comment) => (
                  <Comment
                    key={comment._id}
                    comment={comment}
                    user={comment?.userId}
                  />
                ))}
              </div>
            )}
          </div>
        );
      }}
      defaultOpen={open}
      arrowLeft
    />
  );
};

export default NotesEntry;
